import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  adminApi,
  type CliType,
  type DeviceListItem,
  type DeviceProjectMapEntry,
} from '../api/admin';
import { DataListFrame, sortArrow, stickyTh, useListControls } from '../components/data-list';
import { DEFAULT_TEAM_ICON } from '../lib/team-icon';

// 割当俯瞰モニタ = 全デバイスの全 DIR(device_project_map) を 1 行ずつフラットに並べた読み取り専用一覧。
// アサイン画面がプロジェクト単位の縦割りなのに対し、こちらは「全体でどこに何が割り当たっているか／
// 穴(未アサイン・未束ね・用途未設定・オフライン)はどこか」を一望する俯瞰ビュー。
// 専用 API は持たず listDevices の戻り(project/team/purpose/cliType/enabled・device.online 込み)を素材にする。

const cliLabels: Record<CliType, string> = {
  claude_code: 'Claude Code',
  gemini_cli: 'Gemini CLI',
  cursor_agent: 'Cursor Agent',
};

// 1 DIR を所属デバイス名・オンライン状態つきで持つフラット行。
interface OverviewRow extends DeviceProjectMapEntry {
  deviceName: string;
  deviceOnline: boolean;
}

type OverviewSortField = 'device' | 'project' | 'purpose' | 'team' | 'online';
const SORT_OPTIONS: { value: OverviewSortField; label: string }[] = [
  { value: 'device', label: 'デバイス' },
  { value: 'project', label: 'プロジェクト' },
  { value: 'purpose', label: '用途' },
  { value: 'team', label: 'チーム' },
  { value: 'online', label: 'オンライン' },
];

// 穴(欠落)を示す注意色チップ。未アサイン/未束ね/用途未設定をパッと見えるようにする。
function GapChip({ label }: { label: string }) {
  return (
    <span
      style={{
        padding: '1px 7px',
        borderRadius: 999,
        background: '#fef3c7',
        color: '#92400e',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function Badge({ name, bg, fg }: { name: string; bg: string; fg: string }) {
  return (
    <span style={{ padding: '1px 7px', borderRadius: 999, background: bg, color: fg, fontSize: 11 }}>
      {name}
    </span>
  );
}

// フィルタトグル(ON/OFF)。穴だけ・オフラインだけ等を素早く絞り込む。
function FilterToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-ghost"
      style={{
        padding: '5px 10px',
        fontSize: 12.5,
        borderRadius: 999,
        background: active ? '#eaf1ff' : 'transparent',
        color: active ? '#1d4ed8' : '#475569',
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

// 1 列ぶんの絞り込みドロップダウン。先頭に「すべて」（=絞り込み解除）を必ず置く。
function FilterSelect({
  label,
  value,
  onChange,
  wide,
  children,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: '6px 10px',
          fontSize: 13,
          minWidth: wide ? 240 : 150,
          maxWidth: wide ? 360 : 200,
        }}
      >
        <option value="">すべて</option>
        {children}
      </select>
    </label>
  );
}

export function MonitorOverviewPage() {
  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: adminApi.listDevices });
  const devices = (devicesQuery.data ?? []) as DeviceListItem[];

  // 全 DIR をフラット化（未束ね・未アサインも含めて全部出す＝穴の可視化が目的）。
  const rows = useMemo<OverviewRow[]>(() => {
    const out: OverviewRow[] = [];
    for (const d of devices) {
      const deviceName = d.nickname || d.name;
      for (const e of d.deviceProjectMap) {
        out.push({ ...e, deviceName, deviceOnline: d.online });
      }
    }
    return out;
  }, [devices]);

  // 穴フィルタ（非永続・このページ内のトグル）。
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [onlyUnbundled, setOnlyUnbundled] = useState(false);
  const [onlyNoPurpose, setOnlyNoPurpose] = useState(false);
  const [onlyOffline, setOnlyOffline] = useState(false);

  // 列ごとの絞り込み（''＝すべて）。デバイス/プロジェクト/チームは id、パス/用途は文字列で持つ。
  const [deviceFilter, setDeviceFilter] = useState<number | ''>('');
  const [pathFilter, setPathFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<number | ''>('');
  const [teamFilter, setTeamFilter] = useState<number | ''>('');
  const [purposeFilter, setPurposeFilter] = useState<string>('');

  const controls = useListControls<OverviewSortField>('konecco.list.overview', {
    q: '',
    sortBy: 'device',
    sortDir: 'asc',
    page: 1,
    pageSize: 50,
  });

  // 絞り込みを変えたら 1 ページ目に戻す（件数ズレで空ページにならないように）。
  const resetPage = () => controls.setPage(1);

  // セレクトの選択肢は実データ（rows）から重複排除して作る。0件のデバイス/PJ/チームは出さない。
  const options = useMemo(() => {
    const dev = new Map<number, string>();
    const path = new Set<string>();
    const proj = new Map<number, string>();
    const team = new Map<number, string>();
    const purpose = new Set<string>();
    for (const r of rows) {
      dev.set(r.deviceId, r.deviceName);
      path.add(r.mountPoint.path);
      if (r.projectId != null) proj.set(r.projectId, r.project?.name ?? `project#${r.projectId}`);
      if (r.teamId != null)
        team.set(
          r.teamId,
          `${r.team?.icon || DEFAULT_TEAM_ICON} ${r.team?.name ?? `team#${r.teamId}`}`,
        );
      if (r.purpose) purpose.add(r.purpose);
    }
    const byName = (a: [number, string], b: [number, string]) => a[1].localeCompare(b[1]);
    return {
      devices: [...dev.entries()].sort(byName),
      paths: [...path].sort((a, b) => a.localeCompare(b)),
      projects: [...proj.entries()].sort(byName),
      teams: [...team.entries()].sort(byName),
      purposes: [...purpose].sort((a, b) => a.localeCompare(b)),
    };
  }, [rows]);

  const anyFilterActive =
    onlyUnassigned ||
    onlyUnbundled ||
    onlyNoPurpose ||
    onlyOffline ||
    deviceFilter !== '' ||
    pathFilter !== '' ||
    projectFilter !== '' ||
    teamFilter !== '' ||
    purposeFilter !== '' ||
    controls.q.trim() !== '';

  const clearFilters = () => {
    setOnlyUnassigned(false);
    setOnlyUnbundled(false);
    setOnlyNoPurpose(false);
    setOnlyOffline(false);
    setDeviceFilter('');
    setPathFilter('');
    setProjectFilter('');
    setTeamFilter('');
    setPurposeFilter('');
    controls.setQ('');
  };

  const filtered = useMemo(() => {
    const q = controls.q.trim().toLowerCase();
    let list = rows;
    if (q)
      list = list.filter((r) =>
        [r.deviceName, r.mountPoint.path, r.mountPoint.name, r.purpose ?? '', r.project?.name ?? '', r.team?.name ?? '']
          .join(' ')
          .toLowerCase()
          .includes(q),
      );
    // 列ドロップダウンの絞り込み（選択時のみ）。
    if (deviceFilter !== '') list = list.filter((r) => r.deviceId === deviceFilter);
    if (pathFilter !== '') list = list.filter((r) => r.mountPoint.path === pathFilter);
    if (projectFilter !== '') list = list.filter((r) => r.projectId === projectFilter);
    if (teamFilter !== '') list = list.filter((r) => r.teamId === teamFilter);
    if (purposeFilter !== '') list = list.filter((r) => r.purpose === purposeFilter);
    // 穴トグル。
    if (onlyUnassigned) list = list.filter((r) => r.teamId == null);
    if (onlyUnbundled) list = list.filter((r) => r.projectId == null);
    if (onlyNoPurpose) list = list.filter((r) => !r.purpose);
    if (onlyOffline) list = list.filter((r) => !r.deviceOnline);

    const dir = controls.sortDir === 'asc' ? 1 : -1;
    const key = (r: OverviewRow) => {
      switch (controls.sortBy) {
        case 'project':
          return r.project?.name ?? '￿'; // 未束ねは末尾へ
        case 'purpose':
          return r.purpose ?? '￿';
        case 'team':
          return r.team?.name ?? '￿';
        case 'online':
          return r.deviceOnline ? '0' : '1';
        default:
          return r.deviceName;
      }
    };
    return [...list].sort((a, b) => key(a).localeCompare(key(b)) * dir);
  }, [
    rows,
    controls.q,
    controls.sortBy,
    controls.sortDir,
    onlyUnassigned,
    onlyUnbundled,
    onlyNoPurpose,
    onlyOffline,
    deviceFilter,
    pathFilter,
    projectFilter,
    teamFilter,
    purposeFilter,
  ]);

  // ページング（クライアント側）。フィルタで件数が減ってもページ外にならないようクランプ。
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / controls.pageSize));
  const page = Math.min(controls.page, totalPages);
  const start = (page - 1) * controls.pageSize;
  const pageRows = filtered.slice(start, start + controls.pageSize);

  // 俯瞰サマリ：全体の穴の数（フィルタ前の母数で出す）。
  const summary = useMemo(() => {
    let unassigned = 0;
    let unbundled = 0;
    let noPurpose = 0;
    let offline = 0;
    for (const r of rows) {
      if (r.teamId == null) unassigned++;
      if (r.projectId == null) unbundled++;
      if (!r.purpose) noPurpose++;
      if (!r.deviceOnline) offline++;
    }
    return { unassigned, unbundled, noPurpose, offline };
  }, [rows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>割当俯瞰</h2>
        <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
          全デバイスの全 DIR を 1 行ずつ横断表示します（読み取り専用）。割当の変更は「アサイン」、束ね/用途は
          「プロジェクト」「デバイス」画面で行います。
        </p>
      </div>

      {/* 列ドロップダウン絞り込み：デバイス / パス / 用途 / プロジェクト / 担当チーム。 */}
      <div
        className="k-card"
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          padding: '12px 16px',
        }}
      >
        <FilterSelect label="デバイス" value={deviceFilter} onChange={(v) => { setDeviceFilter(v === '' ? '' : Number(v)); resetPage(); }}>
          {options.devices.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </FilterSelect>
        <FilterSelect label="パス" value={pathFilter} onChange={(v) => { setPathFilter(v); resetPage(); }} wide>
          {options.paths.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </FilterSelect>
        <FilterSelect label="用途" value={purposeFilter} onChange={(v) => { setPurposeFilter(v); resetPage(); }}>
          {options.purposes.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </FilterSelect>
        <FilterSelect label="プロジェクト" value={projectFilter} onChange={(v) => { setProjectFilter(v === '' ? '' : Number(v)); resetPage(); }}>
          {options.projects.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </FilterSelect>
        <FilterSelect label="担当チーム" value={teamFilter} onChange={(v) => { setTeamFilter(v === '' ? '' : Number(v)); resetPage(); }}>
          {options.teams.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </FilterSelect>
        {anyFilterActive && (
          <button type="button" className="btn-ghost" onClick={clearFilters} style={{ padding: '7px 12px', fontSize: 13 }}>
            クリア
          </button>
        )}
      </div>

      <DataListFrame
        title="割当俯瞰"
        total={total}
        loading={devicesQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="検索…（デバイス・パス・用途・プロジェクト・チーム）"
        rightActions={
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <FilterToggle active={onlyUnassigned} onClick={() => setOnlyUnassigned((v) => !v)}>
              未アサイン{summary.unassigned > 0 ? ` ${summary.unassigned}` : ''}
            </FilterToggle>
            <FilterToggle active={onlyUnbundled} onClick={() => setOnlyUnbundled((v) => !v)}>
              未束ね{summary.unbundled > 0 ? ` ${summary.unbundled}` : ''}
            </FilterToggle>
            <FilterToggle active={onlyNoPurpose} onClick={() => setOnlyNoPurpose((v) => !v)}>
              用途未設定{summary.noPurpose > 0 ? ` ${summary.noPurpose}` : ''}
            </FilterToggle>
            <FilterToggle active={onlyOffline} onClick={() => setOnlyOffline((v) => !v)}>
              オフライン{summary.offline > 0 ? ` ${summary.offline}` : ''}
            </FilterToggle>
          </div>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th
                style={{ ...stickyTh, width: 36, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                title="オンライン（クリックで並び替え）"
                onClick={() => controls.toggleSort('online')}
              >
                ●{sortArrow(controls, 'online')}
              </th>
              <th
                style={{ ...stickyTh, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('device')}
                title="クリックで並び替え"
              >
                デバイス{sortArrow(controls, 'device')}
              </th>
              <th style={stickyTh}>パス（DIR）</th>
              <th
                style={{ ...stickyTh, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('purpose')}
                title="クリックで並び替え"
              >
                用途{sortArrow(controls, 'purpose')}
              </th>
              <th
                style={{ ...stickyTh, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('project')}
                title="クリックで並び替え"
              >
                プロジェクト{sortArrow(controls, 'project')}
              </th>
              <th
                style={{ ...stickyTh, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('team')}
                title="クリックで並び替え"
              >
                担当チーム{sortArrow(controls, 'team')}
              </th>
              <th style={stickyTh}>CLI</th>
              <th style={{ ...stickyTh, textAlign: 'center' }}>有効</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr
                key={r.id}
                style={{
                  borderBottom: '1px solid #f1f5f9',
                  opacity: r.enabled ? 1 : 0.55,
                }}
              >
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <span
                    title={r.deviceOnline ? 'オンライン' : 'オフライン'}
                    style={{ fontSize: 10, color: r.deviceOnline ? '#16a34a' : '#cbd5e1' }}
                  >
                    ●
                  </span>
                </td>
                <td style={{ padding: '8px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {r.deviceName}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ fontWeight: 600 }}>{r.mountPoint.name}</div>
                  <code style={{ fontSize: 12, color: '#64748b', wordBreak: 'break-all' }}>
                    {r.mountPoint.path}
                  </code>
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {r.purpose ? (
                    <Badge name={r.purpose} bg="#dcfce7" fg="#166534" />
                  ) : (
                    <GapChip label="用途未設定" />
                  )}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {r.projectId != null ? (
                    <Badge name={r.project?.name ?? `project#${r.projectId}`} bg="#e0e7ff" fg="#3730a3" />
                  ) : (
                    <GapChip label="未束ね" />
                  )}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  {r.teamId != null ? (
                    <Badge
                      name={`${r.team?.icon || DEFAULT_TEAM_ICON} ${r.team?.name ?? `team#${r.teamId}`}`}
                      bg="#f1f5f9"
                      fg="#334155"
                    />
                  ) : (
                    <GapChip label="未アサイン" />
                  )}
                </td>
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#475569' }}>
                  {cliLabels[r.cliType] ?? r.cliType}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  {r.enabled ? (
                    <span style={{ color: '#16a34a' }}>✓</span>
                  ) : (
                    <span className="k-muted" style={{ fontSize: 11 }}>
                      無効
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataListFrame>
    </div>
  );
}
